import { queryKeys } from '@/lib/query-keys';
import { storage } from '@/lib/storage';
import { apiError, authClient } from '@/lib/api/ts-rest';
import { isMfaRequired, type LoginResponse } from '@octave/contracts';
import { decodeAccessToken } from '@/lib/auth/token';
import { createLogger } from '@/lib/logger';
import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const log = createLogger('useAuth');
import { useCallback, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const res = await authClient.login({ body: credentials });
      if (res.status !== 200) throw apiError(res, 'Login failed');
      return res.body;
    },
    onSuccess: (data: LoginResponse) => {
      if (isMfaRequired(data)) {
        startTransition(() => {
          navigate('/verify-mfa', {
            state: {
              challengeId: data.challengeId,
              email: data.email,
            },
            replace: true,
          });
        });
        return;
      }

      storage.set('accessToken', data.accessToken);
      storage.set('refreshToken', data.refreshToken);

      const tokenPayload = decodeAccessToken(data.accessToken);
      if (!tokenPayload) {
        log.error('Login succeeded but the access token is unreadable');
        toast.error('Connexion impossible. Veuillez réessayer.');
        return;
      }
      storage.set('tenantSlug', tokenPayload.tenantSlug);

      Sentry.setUser({ id: tokenPayload.sub, email: tokenPayload.email });
      Sentry.setTag('tenant', tokenPayload.tenantSlug);
      log.info('Login successful', { email: tokenPayload.email, tenant: tokenPayload.tenantSlug });

      queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });

      startTransition(() => {
        navigate(`/o/${tokenPayload.tenantSlug}/home`, { replace: true });
      });
    },
    onError: (error: Error) => {
      if (error.message === 'Account disabled') {
        startTransition(() => {
          navigate('/disabled-user', { replace: true });
        });
        return;
      }

      toast.error('Échec de la connexion. Veuillez vérifier vos identifiants.');
      log.error('Login error', error);
    },
  });

  const logout = () => {
    storage.remove('accessToken');
    storage.remove('refreshToken');
    Sentry.setUser(null);
    queryClient.clear();
    log.info('User logged out');
    navigate('/login');
  };

  const isAuthenticated = (): boolean => {
    return !!storage.get('accessToken');
  };

  const registerWithInvitation = useCallback(
    async ({
      email,
      firstName,
      lastName,
      password,
      invitationId,
    }: {
      email: string;
      firstName: string;
      lastName: string;
      password: string;
      invitationId: string;
    }) => {
      try {
        const res = await authClient.registerWithInvitation({
          body: { email, firstName, lastName, password, invitationId },
        });
        if (res.status !== 201)
          throw apiError(res, 'Registration failed');
        const response = res.body;

        // After registration with invitation, user is logged in
        const { accessToken } = response;
        storage.set('accessToken', accessToken);
        storage.set('refreshToken', response.refreshToken);

        const tokenPayload = decodeAccessToken(accessToken);
        if (!tokenPayload) {
          log.error('Registration succeeded but the access token is unreadable');
          toast.error('Inscription impossible. Veuillez réessayer.');
          return response;
        }
        storage.set('tenantSlug', tokenPayload.tenantSlug);

        Sentry.setUser({ id: tokenPayload.sub, email: tokenPayload.email });
        Sentry.setTag('tenant', tokenPayload.tenantSlug);
        log.info('Registration successful', { tenant: tokenPayload.tenantSlug });

        queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });

        startTransition(() => {
          navigate(`/o/${tokenPayload.tenantSlug}/home`, { replace: true });
        });

        return response;
      } catch (error) {
        log.error('Registration error', error);
        throw error;
      }
    },
    [navigate, queryClient]
  );

  return {
    login: loginMutation.mutate,
    logout,
    isAuthenticated,
    isLoading: loginMutation.isPending,
    error: loginMutation.error,
    registerWithInvitation,
  };
};
